import MuiFormControlLabel from "@mui/material/FormControlLabel";
import MuiRadio from "@mui/material/Radio";
import { useId } from "react";
import { CircleIcon, SuccessIcon, Typography } from "../../General";
import { Stack } from "../../Layout";
import { FormHelperText } from "../_partials";
import styles from "./RadioItem.module.scss";
import { RadioOption } from "./types";

declare module "@mui/material/Radio" {
    interface RadioPropsColorOverrides {
        primaryLink?: true;
    }
}

interface RadioItemProps<OptionType extends RadioOption> {
    disabled?: boolean;
    description?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    label: string;
    renderOption?: (option: OptionType, isSelected: boolean) => React.ReactNode;
    selectedValue: string;
    value: string;
}

export function RadioItem<OptionType extends RadioOption>({
    disabled = false,
    description,
    inputRef,
    label,
    renderOption,
    selectedValue,
    value,
}: RadioItemProps<OptionType>) {
    const id = useId();
    const option = { description, label, value } as OptionType;

    const icon = disabled ? (
        <CircleIcon className={styles["label--is-disabled__icon"]} />
    ) : undefined;

    const isSelected = value === selectedValue;

    return (
        <MuiFormControlLabel
            className={`${styles["label"]} ${disabled ? styles["label--is-disabled"] : ""}`}
            control={
                <MuiRadio
                    aria-labelledby={id}
                    checkedIcon={
                        renderOption ? (
                            <CustomOptionWrapper isActive>
                                {renderOption(option, isSelected)}
                            </CustomOptionWrapper>
                        ) : undefined
                    }
                    classes={{ root: renderOption ? styles["custom-option__root"] : undefined }}
                    color="primaryLink"
                    disabled={disabled}
                    icon={
                        renderOption ? (
                            <CustomOptionWrapper>
                                {renderOption(option, isSelected)}
                            </CustomOptionWrapper>
                        ) : (
                            icon
                        )
                    }
                    id={id}
                    inputRef={inputRef}
                />
            }
            disableTypography
            htmlFor={id}
            label={
                renderOption ? null : (
                    <Typography className={styles["label-text"]} component="span">
                        <Stack component="span">
                            {label}
                            {description ? (
                                <FormHelperText component="span">{description}</FormHelperText>
                            ) : null}
                        </Stack>
                    </Typography>
                )
            }
            value={value}
        />
    );
}

type CustomOptionWrapperProps = {
    children: React.ReactNode;
    isActive?: boolean;
};

function CustomOptionWrapper({ children, isActive = false }: CustomOptionWrapperProps) {
    return (
        <span
            className={`${styles["custom-option"]}  ${
                isActive ? styles["custom-option--is-active"] : ""
            }`}
        >
            {isActive ? <SuccessIcon size="small" /> : null}
            {children}
        </span>
    );
}
